import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import api from "../api/client";
import { AuthResponse } from "../types/auth";
import { User } from "../types";
import { auth } from "../firebase"; // المسار حسب مكان الملف
import { useAnalytics } from "./AnalyticsContext";

interface RefreshResponse {
  access: string;
  refresh: string;
}

interface UsernameCheckResponse {
  available: boolean;
  detail: string;
}

interface AuthContextType {
  user: User | null;
    setUser: React.Dispatch<React.SetStateAction<User | null>>; // ✅ أضفناها هنا

  isLoading: boolean;
  login: (
    identifier: string,
    password: string,
    onSuccess?: () => void
  ) => Promise<boolean>;
  logout: (onSuccess?: () => void) => Promise<void>;
  register: (
    username: string,
    password: string,
    confirmPassword: string,
    onSuccess?: () => void
  ) => Promise<void>;
  checkUsernameAvailability: (
    username: string
  ) => Promise<UsernameCheckResponse>;
  loginWithFirebase: (onSuccess?: () => void) => Promise<boolean>; // 👈 جديدة
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ACCESS_KEY = "access_token";
const REFRESH_KEY = "refresh_token";
const USER_KEY = "user";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { trackEvent } = useAnalytics(); // نستخدم الـ analytics hook

  const setAuthHeader = (token: string) => {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  };

  const clearAuth = () => {
    [ACCESS_KEY, REFRESH_KEY, USER_KEY].forEach((key) =>
      localStorage.removeItem(key)
    );
    delete api.defaults.headers.common["Authorization"];
    setUser(null);
  };

  const saveTokens = (access: string, refresh: string) => {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
    setAuthHeader(access);
  };

  const saveUser = (userData: User) => {
    setUser(userData);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
  };
  // 🔁 Keep user in sync with localStorage
useEffect(() => {
  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } else {
    // localStorage.removeItem(USER_KEY);
  }
}, [user]);

useEffect(() => {
  const initialize = async () => {
    try {
      const storedUser = localStorage.getItem(USER_KEY);
      const access = localStorage.getItem(ACCESS_KEY);
      const refresh = localStorage.getItem(REFRESH_KEY);

      if (access) {
        setAuthHeader(access);
      }

      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }

      // ✅ لو مفيش access بس فيه refresh نحاول نعمل refresh
      if (!access && refresh) {
        const refreshed = await refreshToken();
        if (refreshed) {
          await fetchUserProfile();
        }
      }
    } catch (err) {
      console.error("Initialization error:", err);
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  };

  initialize();
}, []);

  const loginWithFirebase = async (
    onSuccess?: () => void
  ): Promise<boolean> => {
    try {
      const currentUser = auth.currentUser;

      if (!currentUser) throw new Error("No Firebase user signed in");

      // ⬇️ خد الـ idToken من Firebase
      const idToken = await currentUser.getIdToken();
      // console.log(idToken);
      
      // ⬇️ ابعته لـ Django endpoint
      const { data } = await api.post<AuthResponse>(
        "/auth/firebase-login/",
        { idToken },
        // { withCredentials: true }
      );


      saveTokens(data.access, data.refresh);
      saveUser(data.user);

      onSuccess?.();
      return true;
    } catch (err) {
      // handleError("Firebase Login", err);
      // clearAuth();
      // console.log();
      
      return false;
    }
  };
  const fetchUserProfile = async (): Promise<boolean> => {
    try {
      const { data } = await api.get<User>("/auth/profile/", {
        withCredentials: true,
      });
      saveUser(data);
      return true;
    } catch (err) {
      handleError("Fetching profile", err);
      return false;
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    const refresh = localStorage.getItem(REFRESH_KEY);
    if (!refresh) return false;

    try {
      const { data } = await api.post<RefreshResponse>(
        "/auth/token/refresh/",
        { refresh },
        { withCredentials: true }
      );

      saveTokens(data.access, data.refresh || refresh);
      return true;
    } catch (err) {
      handleError("Token refresh", err);
      return false;
    }
  };

  const login = async (
    identifier: string,
    password: string,
    onSuccess?: () => void
  ): Promise<boolean> => {
    try {
      const { data } = await api.post<AuthResponse>(
        "/auth/login/",
        { identifier, password },
        { withCredentials: true }
      );

      saveTokens(data.access, data.refresh);

      const success = await fetchUserProfile();
      if (success) {
        onSuccess?.();
        return true;
      }

      return false;
    } catch (err) {
      handleError("Login", err);
      clearAuth();
      return false;
    }
  };

  const logout = async (onSuccess?: () => void) => {
    try {
      await api.post("/auth/logout/", {}, { withCredentials: true });
      onSuccess?.();
    } catch (err) {
      handleError("Logout", err);
    } finally {
      clearAuth();
    }
  };

  const register = async (
    username: string,
    password: string,
    confirmPassword: string,
    onSuccess?: () => void
  ) => {
    try {
      await api.post(
        "/auth/register/",
        { username, password, password2: confirmPassword },
        { withCredentials: true }
      );
      trackEvent("user_register", { username });

      await login(username, password, onSuccess);
    } catch (err) {
      handleError("Register", err);
      throw err;
    }
  };

  const checkUsernameAvailability = async (
    username: string
  ): Promise<UsernameCheckResponse> => {
    try {
      const { data } = await api.get<UsernameCheckResponse>(
        "/auth/check-username/",
        {
          params: { username },
        }
      );
      return data;
    } catch (err) {
      handleError("Username Check", err);
      throw err;
    }
  };

  const handleError = (label: string, err: unknown) => {
    console.error(`${label} error:`, err);
    clearAuth();
  };

  useEffect(() => {
    const initialize = async () => {
      // try {
      // const refreshed = await refreshToken()
      // if (refreshed) {
      //   await fetchUserProfile()
      // } else {
      const storedUser = localStorage.getItem(USER_KEY);
      const access = localStorage.getItem(ACCESS_KEY);
      if (storedUser && access) {
        setAuthHeader(access);
        setUser(JSON.parse(storedUser));
      }
    };
    // } catch (err) {
    //   handleError('Initialization', err)
    // } finally {
    //   setIsLoading(false)
    // }
    // }

    initialize();
  }, []);
  useEffect(() => {
    if (user) {
      // لو فيه user -> login أو update profile
      trackEvent("user_login", { user_id: user.id, username: user.username });
    } else {
      // user = null -> logout
      trackEvent("user_logout");
    }
  }, [user]);
  return (
    <AuthContext.Provider
      value={{
        user,
              setUser, // ✅ هنا كمان

        isLoading,
        login,
        logout,
        register,
        checkUsernameAvailability,
        loginWithFirebase, // ✅ أهو
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
